import {
  FieldChangeApi,
  LineItemChangesApi,
  ProposalPromoteDiffApiResponse,
  StrategyDiffApi
} from 'shared/src/api/strategy-api-types';
import { formatDateString } from 'shared/src/utils/date/date-utils';
import { formatMoney } from 'shared/src/utils/money/money-utils';
import { formatPct } from 'shared/src/utils/number/number-utils';
import { isLineItemUnpriced } from 'shared/src/domain/line-item/line-item-utils';

export type NewField = {
  label: string;
  value: string | undefined;
};

export type UpdatedField = {
  label: string;
  oldValue: string | undefined;
  newValue: string | undefined;
};

export type DeletedField = {
  label: string;
  value: string | undefined;
};

type NewStrategy = {
  type: 'new';
  items: NewField[];
};

type UpdatedStrategy = {
  type: 'update';
  items: UpdatedField[];
};

export type NewLineItem = {
  id: string;
  name: string;
  type: 'new';
  items: NewField[];
};

export type UpdatedLineItem = {
  id: string;
  name: string;
  type: 'update';
  items: UpdatedField[];
};

export type DeletedLineItem = {
  id: string;
  name: string;
  type: 'delete';
  items: DeletedField[];
  blocked_by_media_buys: boolean;
};

type LineItemChanges = Array<NewLineItem | UpdatedLineItem | DeletedLineItem>;

type StringFieldChange = {
  field: string;
  old: string | undefined;
  new: string | undefined;
};

type NumberFieldChange = {
  field: string;
  old: number | undefined;
  new: number | undefined;
};

type NumberArrayFieldChange = {
  field: string;
  old: number[] | undefined;
  new: number[] | undefined;
};

type ObjectWithNameArrayFieldChange = {
  field: string;
  old: { name: string }[] | undefined;
  new: { name: string }[] | undefined;
};

const stringFields = [
  'campaign_id',
  'pricing_mode',
  'line_item_type',
  'channel',
  'tactic',
  'unit_price_type',
  'name',
  'audience',
  'pacing_type',
  'geo',
  'targeting',
  'ad_formats',
  'purchase_order_number',
  'notes'
];

const dateFields = ['start_date', 'end_date'];

const moneyFields = [
  'gross_price',
  'referral_commission_amount',
  'effective_price',
  'price',
  'planned_unit_cost',
  'unit_price',
  'effective_price',
  'referral_commission_amount',
  'effective_unit_price',
  'gross_unit_price',
  'target_unit_price',
  'target_unit_cost',
  'media_budget'
];

const pctFields = [
  'agency_markup_rate',
  'referral_commission_rate',
  'planned_margin',
  'target_margin'
];

const lookupFields = ['channel_id', 'tactic_id', 'unit_price_type_id'];

const arrayFields = ['media_platforms'];

export function proposalHasChanges(diff: ProposalPromoteDiffApiResponse) {
  return hasStrategyChanges(diff) || hasLineItemChanges(diff);
}

export function hasBlockedDeletions(diff: ProposalPromoteDiffApiResponse) {
  return diff.line_items.to_delete.some(item => item.blocked_by_media_buys);
}

export function hasUnpricedLineItems(
  lineItems: {
    line_item_type: string;
    unit_price: number | null;
    planned_unit_cost?: number | null;
  }[]
): boolean {
  return lineItems.some(isLineItemUnpriced);
}

export function changedStrategyFields(changes: StrategyDiffApi): NewStrategy | UpdatedStrategy {
  return !changes.exists_in_execution
    ? { type: 'new', items: changes.field_changes.map(transformNewField) }
    : { type: 'update', items: changes.field_changes.map(transformUpdatedField) };
}

// TODO[mk] pass a ctx with channels and user names
export function changedLineItemFields(changes: LineItemChangesApi): LineItemChanges {
  const { to_create, to_update, to_delete } = changes;

  const newLineItems: NewLineItem[] = to_create.map(lineItem => ({
    id: lineItem.id,
    name: lineItem.name,
    type: 'new',
    items: lineItem.field_changes.map(transformNewField)
  }));

  const updatedLineItems: UpdatedLineItem[] = to_update.map(lineItem => ({
    id: lineItem.id,
    name: lineItem.name,
    type: 'update',
    items: lineItem.field_changes.map(transformUpdatedField)
  }));

  const deletedLineItems: DeletedLineItem[] = to_delete.map(lineItem => ({
    id: lineItem.id,
    name: lineItem.name,
    type: 'delete',
    items: [{ label: 'Name', value: lineItem.name }],
    blocked_by_media_buys: lineItem.blocked_by_media_buys
  }));

  return [...newLineItems, ...updatedLineItems, ...deletedLineItems];
}

function transformNewField(field: FieldChangeApi): NewField {
  const label = transformLabel(field.field);
  if (isStringField(field)) {
    return { label, value: field.new || '-' };
  } else if (isDateField(field)) {
    return { label, value: formatDateString(field.new) };
  } else if (isMoneyField(field)) {
    return { label, value: formatMoney(field.new) };
  } else if (isPctField(field)) {
    return { label, value: formatPct(field.new) };
  } else if (isLookupField(field)) {
    return { label, value: lookupField(field.new) };
  } else if (isNumberArrayField(field)) {
    return { label, value: lookupArrayField(field.new) };
  } else if (isObjectWithNameArrayField(field)) {
    return {
      label,
      value: readObjectWitNameArrayField(field.new)
    };
  } else {
    return { label, value: '------' };
  }
}

function transformUpdatedField(field: FieldChangeApi): UpdatedField {
  const label = transformLabel(field.field);
  if (isStringField(field)) {
    return { label, oldValue: field.old || '-', newValue: field.new || '-' };
  } else if (isDateField(field)) {
    return { label, oldValue: formatDateString(field.old), newValue: formatDateString(field.new) };
  } else if (isMoneyField(field)) {
    return { label, oldValue: formatMoney(field.old), newValue: formatMoney(field.new) };
  } else if (isPctField(field)) {
    return { label, oldValue: formatPct(field.old), newValue: formatPct(field.new) };
  } else if (isLookupField(field)) {
    return { label, oldValue: lookupField(field.old), newValue: lookupField(field.new) };
  } else if (isNumberArrayField(field)) {
    return { label, oldValue: lookupArrayField(field.old), newValue: lookupArrayField(field.new) };
  } else if (isObjectWithNameArrayField(field)) {
    return {
      label,
      oldValue: readObjectWitNameArrayField(field.old),
      newValue: readObjectWitNameArrayField(field.new)
    };
  } else {
    return { label, oldValue: '------', newValue: '------' };
  }
}

function isStringField(fieldChange: FieldChangeApi): fieldChange is StringFieldChange {
  return (
    stringFields.includes(fieldChange.field) &&
    isStringOrNullish(fieldChange.new) &&
    isStringOrNullish(fieldChange.old)
  );
}

function isDateField(fieldChange: FieldChangeApi): fieldChange is StringFieldChange {
  return (
    dateFields.includes(fieldChange.field) &&
    isStringOrNullish(fieldChange.new) &&
    isStringOrNullish(fieldChange.old)
  );
}

function isMoneyField(fieldChange: FieldChangeApi): fieldChange is NumberFieldChange {
  return (
    moneyFields.includes(fieldChange.field) &&
    isNumberOrNullish(fieldChange.new) &&
    isNumberOrNullish(fieldChange.old)
  );
}

function isPctField(fieldChange: FieldChangeApi): fieldChange is NumberFieldChange {
  return (
    pctFields.includes(fieldChange.field) &&
    isNumberOrNullish(fieldChange.new) &&
    isNumberOrNullish(fieldChange.old)
  );
}

function isLookupField(fieldChange: FieldChangeApi): fieldChange is NumberFieldChange {
  return (
    lookupFields.includes(fieldChange.field) &&
    isNumberOrNullish(fieldChange.new) &&
    isNumberOrNullish(fieldChange.old)
  );
}

function isObjectWithNameArrayField(
  fieldChange: FieldChangeApi
): fieldChange is ObjectWithNameArrayFieldChange {
  return (
    arrayFields.includes(fieldChange.field) &&
    isObjectArrayOrNullish(fieldChange.new) &&
    isObjectArrayOrNullish(fieldChange.old)
  );
}

function isNumberArrayField(fieldChange: FieldChangeApi): fieldChange is NumberArrayFieldChange {
  return (
    arrayFields.includes(fieldChange.field) &&
    isNumberArrayOrNullish(fieldChange.new) &&
    isNumberArrayOrNullish(fieldChange.old)
  );
}

function lookupField(value: number | undefined | null) {
  return value?.toLocaleString() || '-';
}

function lookupArrayField(value: number[] | undefined | null) {
  return value && value.length > 0 ? value.map(v => v.toLocaleString()).join(', ') : '-';
}

function readObjectWitNameArrayField(value: { name: string }[] | undefined | null) {
  return value && value.length > 0 ? value.map(v => v.name.toLocaleString()).join(', ') : '-';
}

function transformLabel(field: string): string {
  return field
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function hasStrategyChanges(diff: ProposalPromoteDiffApiResponse) {
  return !diff.strategy.exists_in_execution || diff.strategy.field_changes.length > 0;
}

function hasLineItemChanges(diff: ProposalPromoteDiffApiResponse) {
  const { to_create, to_update, to_delete } = diff.line_items;
  return to_create.length > 0 || to_update.length > 0 || to_delete.length > 0;
}

function isStringOrNullish(value: unknown): value is string | undefined {
  return typeof value === 'string' || value === undefined || value === null;
}

function isNumberOrNullish(value: unknown): value is number | undefined {
  return typeof value === 'number' || value === undefined || value === null;
}

function isNumberArrayOrNullish(value: unknown): value is number[] {
  return (
    (Array.isArray(value) && value.every(x => typeof x === 'number')) ||
    value === undefined ||
    value === null
  );
}

function isObjectArrayOrNullish(value: unknown): value is number[] {
  return (
    (Array.isArray(value) && value.every(x => typeof x === 'object')) ||
    value === undefined ||
    value === null
  );
}
